import { useCallback, useState } from 'react';
import { parsePlanLimit, planLimitMessageKey, usePlan } from '@/shared/billing';
import { PLAN_LABEL_KEY, type ModulePlan } from '@/shared/nav/modules';
import type { MessageKey } from '@/shared/i18n/messages';
import { useI18n } from '@/shared/i18n/useT';

/**
 * Turn whatever the database refused into a sentence the user can act on.
 *
 * Two cases and no third: a plan ceiling, which carries its real number and a
 * reason, and everything else, which gets the caller's own message. What must
 * never happen is the silent failure — `src/modules/AGENTS.md` requires a
 * visible state for every write that does not land.
 */
export function useFoldersErrors() {
  const { t } = useI18n();
  const plan = usePlan();
  const [message, setMessage] = useState<string | null>(null);

  const planLabel = (plan.planCode in PLAN_LABEL_KEY)
    ? t(PLAN_LABEL_KEY[plan.planCode as ModulePlan])
    : plan.planCode;

  const report = useCallback(
    (cause: unknown, fallbackKey: MessageKey) => {
      const limit = parsePlanLimit(cause);
      setMessage(
        limit
          ? t(planLimitMessageKey(limit), { plan: planLabel, limit: limit.limit })
          : t(fallbackKey),
      );
    },
    [t, planLabel],
  );

  const clear = useCallback(() => setMessage(null), []);

  return { message, report, clear };
}


